import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tidbitApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2, Sparkles, ExternalLink, Target, Clock, User, Calendar, Star } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Tidbit } from '@/types/learningManagement';
import type { Skill } from '@/types/skillKnowledge';

export const TidbitDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();
  const [skill, setSkill] = useState<Skill | null>(null);

  const canEdit = hasPermission('edit', 'tidbit');
  const canDelete = hasPermission('delete', 'tidbit');

  // Use entity hook for fetching tidbit
  const {
    data: tidbit,
    isLoading,
    error,
    refetch,
  } = useEntity<Tidbit>({
    fetchFn: (tidbitId: string) => tidbitApi.getById(tidbitId),
    entityId: id,
    entityName: 'tidbit',
    enabled: !!id,
  });

  // Fetch skill details if skillId exists
  useEffect(() => {
    if (!tidbit?.skillId) {
      setSkill(null);
      return;
    }

    skillApi.getById(tidbit.skillId)
      .then(setSkill)
      .catch((err) => {
        console.error('Error loading skill:', err);
        setSkill(null);
      });
  }, [tidbit?.skillId]);

  const isSystemDefined = tidbit?.tidbitGenerated === 'SYSTEM_DEFINED';
  const canModify = canEdit && (!isSystemDefined || hasPermission('system_admin', 'tidbit'));

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Tidbit ID is required');
      await tidbitApi.delete(id);
    },
    entityName: 'tidbit',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/tidbits';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this tidbit?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading tidbit..." />;
  }

  if (error || !tidbit) {
    return <ErrorState error={error || new Error('Tidbit not found')} onRetry={refetch} />;
  }

  const tidbitId = tidbit.tidbitId || tidbit.id || '';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': case 'PUBLISHED': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED': case 'EXPIRED': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'FEATURED': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const getInterestColor = (level: string) => {
    switch (level) {
      case 'MIND_BLOWING': return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200';
      case 'FASCINATING': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'MODERATE': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'LOW': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getDifficultyColor = (level?: string) => {
    switch (level) {
      case 'ELEMENTARY': case 'BASIC': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'INTERMEDIATE': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ADVANCED': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'EXPERT': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/tidbits">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-amber-500" />
              <h1 className="text-3xl font-bold tracking-tight">{tidbit.title}</h1>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tidbit.status)}`}>
                {tidbit.status}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getInterestColor(tidbit.interestLevel)}`}>
                <Star className="inline h-3 w-3 mr-1" />
                {tidbit.interestLevel.replace(/_/g, ' ')}
              </span>
              {tidbit.difficultyLevel && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(tidbit.difficultyLevel)}`}>
                  {tidbit.difficultyLevel}
                </span>
              )}
              {isSystemDefined && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  System Defined
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {canModify && (
            <Button asChild>
              <Link to={`/admin/tidbits/${tidbitId}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && !isSystemDefined && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Core tidbit details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={tidbitId} label="Tidbit ID" />
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1 text-sm">{tidbit.description}</p>
            </div>
            {tidbit.preview && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Preview</label>
                <p className="mt-1 text-sm italic text-muted-foreground">{tidbit.preview}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Content</label>
              <p className="mt-1 text-sm bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">{tidbit.content}</p>
            </div>
          </CardContent>
        </Card>

        {/* Classification */}
        <Card>
          <CardHeader>
            <CardTitle>Classification</CardTitle>
            <CardDescription>Category and type information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Category</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-sm">
                    {tidbit.category.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Tidbit Type</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-sm">
                    {tidbit.tidbitType.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
            </div>
            {tidbit.subCategory && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Sub Category</label>
                <p className="mt-1 text-sm">{tidbit.subCategory}</p>
              </div>
            )}
            {tidbit.mediaType && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Media Type</label>
                <p className="mt-1">
                  <span className="px-2 py-1 bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200 rounded text-sm">
                    {tidbit.mediaType.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
            )}
            {tidbit.consumptionTimeMinutes && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Consumption Time</label>
                <p className="mt-1 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{tidbit.consumptionTimeMinutes} minutes</span>
                </p>
              </div>
            )}
            {tidbit.targetAudience && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Target Audience</label>
                <p className="mt-1 flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{tidbit.targetAudience}</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Related Skill */}
        {tidbit.skillId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Related Skill
              </CardTitle>
              <CardDescription>Skill associated with this tidbit</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Link
                    to={`/admin/skills/${tidbit.skillId}`}
                    className="font-medium text-blue-600 hover:underline flex items-center gap-2"
                  >
                    {skill?.name || 'View Skill'}
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
                <CopyableId id={tidbit.skillId} label="Skill ID" />
                {skill?.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{skill.description}</p>
                )}
                {skill?.category && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                    {skill.category.replace(/_/g, ' ')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Schedule Information */}
        {(tidbit.publicationDate || tidbit.expirationDate) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Schedule
              </CardTitle>
              <CardDescription>Publication and expiration dates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {tidbit.publicationDate && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Publication Date</label>
                    <p className="mt-1 text-sm">{new Date(tidbit.publicationDate).toLocaleString()}</p>
                  </div>
                )}
                {tidbit.expirationDate && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Expiration Date</label>
                    <p className="mt-1 text-sm">{new Date(tidbit.expirationDate).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Author Information */}
        {tidbit.authorInfo && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Author Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {tidbit.authorInfo.authorName && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Name</label>
                    <p className="mt-1 text-sm">{tidbit.authorInfo.authorName}</p>
                  </div>
                )}
                {tidbit.authorInfo.authorEmail && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="mt-1 text-sm">{tidbit.authorInfo.authorEmail}</p>
                  </div>
                )}
                {tidbit.authorInfo.authorId && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Author ID</label>
                    <p className="mt-1 font-mono text-sm">{tidbit.authorInfo.authorId}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit Information */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {tidbit.createdAt ? new Date(tidbit.createdAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {tidbit.updatedAt ? new Date(tidbit.updatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {tidbit.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{tidbit.createdBy}</p>
                </div>
              )}
              {tidbit.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{tidbit.updatedBy}</p>
                </div>
              )}
              {tidbit.version !== undefined && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Version</label>
                  <p className="mt-1 text-sm">{tidbit.version}</p>
                </div>
              )}
              {tidbit.tidbitGenerated && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Generated</label>
                  <p className="mt-1 text-sm">{tidbit.tidbitGenerated.replace(/_/g, ' ')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resources */}
      {tidbit.resources && tidbit.resources.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resources ({tidbit.resources.length})</CardTitle>
            <CardDescription>Additional resources for this tidbit</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {tidbit.resources.map((resource, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200 text-sm"
                >
                  {resource}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tags */}
      {tidbit.tags && tidbit.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tags ({tidbit.tags.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {tidbit.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TidbitDetailPage;
